/**
 * Locale-aware formatters for dates, times, numbers, and currency.
 *
 * Built on native `Intl` — no runtime dependency on date-fns / dayjs. Covers
 * every display-layer formatting need the app currently has. If timezone
 * arithmetic or complex date math becomes necessary later, layer `date-fns-tz`
 * on top; keep this module as the presentation wrapper.
 *
 * Locale-dependent values (BCP-47 locale, currency, language) come from
 * `DEFAULT_LOCALE` (`core/i18n/locale-config.ts`) — Spain today, the single
 * seam for going multi-locale later. The Spanish weekday/month abbreviations
 * below stay hardcoded for now (deterministic short-form output); they move to
 * Intl-derived strings when a second locale lands.
 */
import { DEFAULT_LOCALE } from '@/core/i18n/locale-config'

// Hardcoded Spanish abbreviations so short-form output renders deterministically
// and without the stray punctuation that `toLocaleDateString('es-ES', { ... })`
// sometimes produces (e.g. "jue., 19 abr.").
const WEEKDAYS_ES = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'] as const
const MONTHS_ES = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
] as const

const LOCALE = DEFAULT_LOCALE.intlLocale
const CURRENCY = DEFAULT_LOCALE.currency

// Shared Intl instances — cheaper than re-creating per call, and matter on the
// dashboard where the clock re-renders every second.
const RELATIVE = new Intl.RelativeTimeFormat(DEFAULT_LOCALE.language, { numeric: 'auto' })
const CURRENCY_0 = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: CURRENCY,
  maximumFractionDigits: 0,
})
const CURRENCY_1 = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: CURRENCY,
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
const CURRENCY_2 = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: CURRENCY,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

// ── Clock ──────────────────────────────────────────────────────────────────

export const pad2 = (n: number): string => String(n).padStart(2, '0')

export function formatClock(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/** "07" — zero-padded seconds. Rendered subordinate to HH:MM in the audit log
 *  so same-minute events stay ordered + distinguishable. */
export function formatSeconds(d: Date): string {
  return pad2(d.getSeconds())
}

// ── Dates ──────────────────────────────────────────────────────────────────

/** "19 abr 2026" — list-row date. */
export function formatDate(d: Date): string {
  return d.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * "2026-08-15" — machine-readable local calendar day, for CSV export fields
 * and filename stamps. NOT `d.toISOString().slice(0, 10)` — that truncates
 * via UTC and reads back the day before near local midnight in any
 * positive-UTC-offset zone (Spain included). Uses local getFullYear/Month/Date
 * components instead, same shape as `DateField`'s internal conversion.
 */
export function formatIsoDateLocal(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/**
 * "19 abr 2026" from a `YYYY-MM-DD` day-key string (e.g. a tournament's
 * `dateKey`/`dateKeys`, or a booking's date-key) — the humanized counterpart
 * to raw ISO keys that would otherwise leak to the UI verbatim. Parses the
 * Y/M/D components directly (not `new Date(dateKey)`, which parses as UTC
 * midnight and can render as the previous day in a timezone behind UTC) so
 * the calendar date never shifts regardless of the viewer's local timezone.
 */
export function formatDateKey(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number)
  if (!y || !m || !d) return dateKey
  return formatDate(new Date(y, m - 1, d))
}

/** "19 abr 2026, 09:45" — audit-log / detail-panel date+time. */
export function formatDateTime(d: Date): string {
  return d.toLocaleString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** "jue 19 abr" — reception-friendly topbar date, no punctuation. */
export function formatShortDate(d: Date): string {
  return `${WEEKDAYS_ES[d.getDay()]} ${d.getDate()} ${MONTHS_ES[d.getMonth()]}`
}

/** "jueves, 19 de abril de 2026" — long-form, for `aria-label`s. */
export function formatLongDate(d: Date): string {
  return d.toLocaleDateString(LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/** 'dom' … 'sáb' */
export function formatWeekdayShort(d: Date): string {
  return WEEKDAYS_ES[d.getDay()] ?? ''
}

/** 'ene' … 'dic' */
export function formatMonthShort(d: Date): string {
  return MONTHS_ES[d.getMonth()] ?? ''
}

/** "29 may" — zero-padded day + short month, for compact list "Cuándo" columns
 *  (the Actividad table + the Hoy "Última actividad" glance share this). */
export function formatDayMonth(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS_ES[d.getMonth()] ?? ''}`
}

// ── Relative time ──────────────────────────────────────────────────────────

/**
 * "hace 2 días", "ayer", "hace 3 minutos", "en 5 horas".
 *
 * Uses `Intl.RelativeTimeFormat` with `numeric: 'auto'` so near-past/future
 * values collapse to natural phrases ("ayer" instead of "hace 1 día").
 * Unit is picked by magnitude — seconds < 1 min, minutes < 1 h, etc. —
 * matching the `timeago` behaviour. Returns `opts.nullLabel` (or `'—'`)
 * when the input is null/undefined.
 */
export function formatRelative(
  d: Date | null | undefined,
  opts?: { nullLabel?: string },
): string {
  if (!d) return opts?.nullLabel ?? '—'
  const diffMs = d.getTime() - Date.now()
  const abs = Math.abs(diffMs)
  const MIN = 60_000
  const HOUR = 60 * MIN
  const DAY = 24 * HOUR
  const WEEK = 7 * DAY
  const MONTH = 30 * DAY
  const YEAR = 365 * DAY

  if (abs < MIN) return RELATIVE.format(Math.round(diffMs / 1000), 'second')
  if (abs < HOUR) return RELATIVE.format(Math.round(diffMs / MIN), 'minute')
  if (abs < DAY) return RELATIVE.format(Math.round(diffMs / HOUR), 'hour')
  if (abs < WEEK) return RELATIVE.format(Math.round(diffMs / DAY), 'day')
  if (abs < MONTH) return RELATIVE.format(Math.round(diffMs / WEEK), 'week')
  if (abs < YEAR) return RELATIVE.format(Math.round(diffMs / MONTH), 'month')
  return RELATIVE.format(Math.round(diffMs / YEAR), 'year')
}

/**
 * "Última reserva / last-seen"-style display. Past dates render relative
 * ("hace 3 días"); a date in the FUTURE renders as an absolute short date
 * ("12 jun") instead of a relative phrase.
 *
 * Why: the CRM rollup's `lastBookingDate` tracks `max(startDateTime)` across
 * ALL bookings (including upcoming ones — that recency is what powers the
 * "active customers first" sort). Under a "Última reserva" label, a future
 * value would read as a nonsensical "dentro de 6 horas". Falling back to the
 * absolute date keeps it factual without the past/future framing.
 */
export function formatLastSeen(
  d: Date | null | undefined,
  opts?: { nullLabel?: string },
): string {
  if (!d) return opts?.nullLabel ?? '—'
  if (d.getTime() > Date.now()) return formatDayMonth(d)
  return formatRelative(d, opts)
}

// ── Numbers ────────────────────────────────────────────────────────────────

export interface CurrencyOpts {
  /** Fraction digits: 0 default. Use 1 for per-hour rates, 2 for invoice lines. */
  digits?: 0 | 1 | 2
}

/**
 * Quantize a money amount to cents (2 dp). Money in this codebase is stored
 * as floating-point euros (not integer minor units), so any *aggregation*
 * that is persisted or crosses a system boundary must be rounded back to
 * cents to stop IEEE-754 residue compounding. Display is already cent-safe
 * (Intl rounds), and the paid/partial decision is epsilon-guarded
 * (`booking.ts` `derivePaymentStatus`) — this is for the few persisted
 * accumulators (e.g. the CRM `totalSpent` `firestore.Increment` delta) and
 * cross-system totals (the Ingresos ledger). Never compare money with
 * `===`; round both sides or use an epsilon.
 *
 * Contract: inputs are sums of ≤2-decimal euro values carrying float
 * residue far below half a cent, so `Math.round(x*100)/100` reliably
 * recovers the intended cents. It is NOT a general half-away rounder for
 * arbitrary 3-dp values (money never has 3 dp here).
 */
export function roundMoney(amount: number): number {
  return Math.round(amount * 100) / 100
}

/**
 * Parse user-typed money into a euro number, locale-tolerant.
 *
 * Spanish users type the decimal separator as a comma (`12,50`); other inputs
 * (or paste) use a dot (`12.50`). Native `type="number"` / `Number()` choke on
 * the comma (NaN), so every money input is `type="text" inputMode="decimal"`
 * and routes its value through here — one place that decides what a separator
 * means.
 *
 * Rules:
 * - Strips currency symbols, spaces and any other non-numeric noise.
 * - Both separators present → the **last** one is the decimal point, the other
 *   is a thousands grouping (`1.234,56` → 1234.56, `1,234.56` → 1234.56).
 * - Only a comma → it's the decimal point (`12,50` → 12.5).
 * - Only a dot → it's the decimal point (`12.50` → 12.5); we don't treat a
 *   lone dot as thousands because in an input it almost always means decimals.
 * - Empty / junk / lone sign → `null` (caller decides: leave blank vs clamp).
 *
 * Does NOT round or clamp — callers apply `roundMoney` / `min` as needed.
 */
export function parseMoneyInput(raw: string | number | null | undefined): number | null {
  if (raw == null) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null

  let s = raw.trim().replace(/[^\d.,-]/g, '')
  if (s === '' || s === '-') return null

  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')
  if (lastComma !== -1 && lastDot !== -1) {
    // Both present: last separator is the decimal, the other is grouping.
    s = lastComma > lastDot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '')
  } else if (lastComma !== -1) {
    s = s.replace(',', '.')
  }

  const n = Number.parseFloat(s)
  return Number.isFinite(n) ? n : null
}

/**
 * Money formatter. Defaults to 2 decimals — euros have cents, and a single
 * amount must read identically on every surface (list row, detail drawer,
 * Cobrar popover, Ingresos ledger). Rounding to whole euros was the source of
 * cross-surface drift (e.g. a 37,50 € booking showing "38 €" in the list but
 * "37,50 €" in the drawer). Opt into compact whole-euro display with
 * `{ digits: 0 }` only where a glanceable KPI genuinely wants it.
 *
 * "1.234,56 €" (default / digits=2), "1.234,5 €" (digits=1), "1.234 €" (digits=0).
 */
export function formatCurrency(amount: number, opts?: CurrencyOpts): string {
  const digits = opts?.digits ?? 2
  const fmt = digits === 2 ? CURRENCY_2 : digits === 1 ? CURRENCY_1 : CURRENCY_0
  // Normalize a value that rounds to zero at the display precision (a tiny
  // negative float residue, or -0) to +0 so it never renders as "-0,00 €".
  // Float spend accumulators (CRM `totalSpent`, booking-minus-venta splits)
  // carry sub-cent residue that Intl would otherwise sign.
  const normalized = Math.round(amount * 10 ** digits) === 0 ? 0 : amount
  return fmt.format(normalized)
}

/** "1.234" — integer with the locale's thousands separator. */
export function formatInteger(n: number): string {
  return Math.round(n).toLocaleString(LOCALE)
}

const MONEY_PLAIN_2 = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const MONEY_EDIT = new Intl.NumberFormat(LOCALE, {
  maximumFractionDigits: 2,
  useGrouping: false,
})

/** "37,50" — money for an editable field AT REST: locale decimals, no currency
 *  symbol (the symbol lives beside the input). Pairs with `parseMoneyInput`. */
export function formatMoneyPlain(n: number): string {
  return MONEY_PLAIN_2.format(n)
}

/** "37,5" — money for an editable field WHILE FOCUSED: no grouping, no forced
 *  trailing zeros, so typing stays natural. `parseMoneyInput` reads it back. */
export function formatMoneyEdit(n: number): string {
  return MONEY_EDIT.format(n)
}

/** "12%", "12,5%". Input is 0–100, not 0–1. */
export function formatPercent(n: number, digits = 0): string {
  return `${n.toFixed(digits).replace('.', ',')}%`
}

/**
 * "1 pareja" · "2 parejas" — count + noun, agreeing in number.
 *
 * Every list caps line in the app builds its own `${n} parejas`, which reads
 * wrong at exactly the moment a club sees it most: the first entrant. The
 * torneo workspace shipped "1 parejas · 3 pistas · 0 jornadas" across three
 * surfaces. Spanish plurals here are regular, so the rule is one `s` — pass
 * `plural` explicitly for the ones that are not (`clasificados/grupo`).
 */
export function pluralize(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`
}
